import { OrkiszewskiFlowRegime } from ".";
import { GRAVITY_FT_SEC } from "../../constants";
import type { PipeFrictionContext } from "../d";
import type { PipeFrictionIntermediates } from "..";

export function calculateGradientsMist(
  tubingFrictionContext: PipeFrictionContext
): PipeFrictionIntermediates {
  const {
    fluidPropertiesContext: {
      fluidProperties: {
        gasSuperficialVelocityFTSEC,
        gasDensityLBMFT3,
        liquidSuperficialVelocityFTSEC,
        liquidDensityLBMFT3,
        liquidViscosityCP
      },
      fluidFrictionProperties: {
        bubbleRiseVelocity,
        liquidInterfacialTension,
        hydraulicDiameterFt,
        noSlipLiquidHoldupLambdaCL
      }
    },
    epsilonD,
    pipeLengthFt,
    pipeDiameterIn,
    pipeRoughnessIn
  } = tubingFrictionContext;

  const reynoldsTwoPhaseNumber_Re =
    (1488 * bubbleRiseVelocity * hydraulicDiameterFt * liquidDensityLBMFT3) /
    liquidViscosityCP;

  // THIS EXPLICITLY REFERENCE CHEN FRICTION IN THE BUBBLE REGIME
  // D230 =(1/(-2*LOG10(D228/3.7065-(5.0452/D224)*LOG10(((D228^1.1098)/2.8257)+(5.8506/(D224^0.8981))))))^2
  const chenFriction =
    (1 /
      (-2 *
        Math.log10(
          epsilonD / 3.7065 -
            (5.0452 / reynoldsTwoPhaseNumber_Re) *
              Math.log10(
                epsilonD ** 1.1098 / 2.8257 +
                  5.8506 / reynoldsTwoPhaseNumber_Re ** 0.8981
              )
        ))) **
    2;

  // D294: =((1/(4*LOG10(0.27*D321))^2)+0.067*D321^1.73)*4
  const frictionFactor_f1 =
    ((1 / (4 * Math.log10(0.27 * epsilonD))) ** 2 + 0.067 * epsilonD) ** 1.73 *
    4;

  // D323: =(D154*(D132/12)^2)/((D132/12)-D5)^2 -- moved from below
  const vsg =
    ((gasSuperficialVelocityFTSEC * (pipeDiameterIn / 12)) ** 2 /
      (pipeDiameterIn / 12 - pipeRoughnessIn)) **
    2;

  // D325: =(D132/12)*SQRT((D67*D63)/D173) -- moved from below
  const pipeDiameterNumber_N_D =
    (pipeDiameterIn / 12) *
    Math.sqrt(
      (GRAVITY_FT_SEC * liquidDensityLBMFT3) / liquidInterfacialTension
    );

  // D317: =D323/D155 -- moved from below
  const gasLiquidVelocityRatio_R = vsg / liquidSuperficialVelocityFTSEC;

  // D296: =D294*D317*D325^(2/3)
  const frictionFactor_f2 =
    (frictionFactor_f1 * gasLiquidVelocityRatio_R * pipeDiameterNumber_N_D) **
    (2 / 3);

  // D298: =1+D254*SQRT(D317/50)
  const frictionFactor_f3 =
    1 + chenFriction * Math.sqrt(gasLiquidVelocityRatio_R / 50);

  // D300: =D294*(D296/D298)
  const frictionFactor_fw =
    frictionFactor_f1 * (frictionFactor_f2 / frictionFactor_f3);

  // D302: =(D300*D62*D154^2)/(2*D67*(D132/12))/144
  const frictionGradient =
    (frictionFactor_fw * gasDensityLBMFT3 * gasSuperficialVelocityFTSEC) ** 2 /
    (2 * GRAVITY_FT_SEC * (pipeDiameterIn / 12)) /
    144;

  // D304: =1/(1+D317)
  const inSituLiquidHoldup_E_l = 1 / (1 + gasLiquidVelocityRatio_R);

  // D310: =(D304*D63+(1-D244)*D62)/(144)
  const staticGradient =
    (inSituLiquidHoldup_E_l * liquidDensityLBMFT3 +
      (1 - noSlipLiquidHoldupLambdaCL) * gasDensityLBMFT3) /
    144;

  // D312: =D302*D3
  const frictionPressurePSIA = frictionGradient * pipeLengthFt;

  // D314: =D310*D3*SIN(D8)  -- renamed from staticPressure
  // const hydrostaticPressurePSIA = staticGradient * hydrostaticSum;
  return {
    flowRegime: OrkiszewskiFlowRegime.MIST,
    frictionPressurePSIA,
    reynoldsTwoPhaseNumber_Re,
    chenFriction,
    frictionFactor_f1,
    vsg,
    pipeDiameterNumber_N_D,
    gasLiquidVelocityRatio_R,
    frictionFactor_f2,
    frictionFactor_f3,
    frictionFactor_fw,
    frictionGradient,
    inSituLiquidHoldup_E_l,
    staticGradient,
    bubbleRiseVelocity,
    hydraulicDiameterFt,
    liquidDensityLBMFT3,
    liquidViscosityCP
  };
}
